import React, { useState, useEffect } from "react";
import axios from "axios";
import { Plus, Edit2, Trash2, X, ChevronDown, ChevronUp, Globe, Percent, Save } from "lucide-react";
import styles from "../hrms.module.scss";
import Popup from "components/minor/popup/popup";

import { apiBaseUrl } from '../../../../../../../services/config';
import { useTheme } from "context/ThemeContext";
const API = apiBaseUrl;
const ax  = () => ({ headers: { Authorization: `Bearer ${sessionStorage.getItem("platform_token") || ""}` } });
const fmt = (n) => n == null ? "∞" : `₹${Number(n).toLocaleString("en-IN")}`;

const COUNTRY_FLAGS = { India:"🇮🇳", Singapore:"🇸🇬", USA:"🇺🇸", UK:"🇬🇧", Malaysia:"🇲🇾", UAE:"🇦🇪", Other:"🌍" };

export default function TaxConfig() {
  const { T } = useTheme();
  const inp = (err=false) => ({
    width:"100%", background:T.inp_bg,
    border:`1px solid ${err?T.red:T.border}`,
    borderRadius:8, color:T.text, fontSize:13,
    padding:"9px 12px", outline:"none", fontFamily:"Outfit,sans-serif", boxSizing:"border-box",
  });
  const lbl = { fontSize:11, color:T.muted, display:"block", marginBottom:4, textTransform:"uppercase", letterSpacing:"0.07em" };

  const [regimes,   setRegimes]   = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [expanded,  setExpanded]  = useState(null);
  const [toast,     setToast]     = useState("");
  const [seeding,   setSeeding]   = useState(false);

  // Regime modal
  const [rOpen, setROpen] = useState(false);
  const [rMode, setRMode] = useState("add");
  const [rId,   setRId]   = useState(null);
  const [rData, setRData] = useState({ name:"", country:"India", financial_year:"2024-25", standard_deduction:0, is_default:false, notes:"" });
  const [delOpen, setDelOpen] = useState(false);
  const [delId,   setDelId]   = useState(null);

  // Slab modal
  const [sOpen,  setSOpen]  = useState(false);
  const [sRegId, setSRegId] = useState(null);
  const [sId,    setSId]    = useState(null);
  const [sMode,  setSMode]  = useState("add");
  const [sData,  setSData]  = useState({ from_amount:0, to_amount:"", rate:0, sort_order:0 });

  // Deduction modal
  const [dOpen,  setDOpen]  = useState(false);
  const [dRegId, setDRegId] = useState(null);
  const [dData,  setDData]  = useState({ name:"", max_amount:0, description:"", sort_order:0 });

  // Rebate modal
  const [rbOpen,  setRbOpen]  = useState(false);
  const [rbRegId, setRbRegId] = useState(null);
  const [rbData,  setRbData]  = useState({ name:"", if_income_below:0, rebate_amount:0, notes:"" });

  // Surcharge modal
  const [scOpen,  setScOpen]  = useState(false);
  const [scRegId, setScRegId] = useState(null);
  const [scData,  setScData]  = useState({ name:"", rate:0, applies_on:"tax" });

  const flash = (msg) => { setToast(msg); setTimeout(() => setToast(""), 3500); };

  const load = async () => {
    setLoading(true);
    try { const r = await axios.get(`${API}/tax-config/`, ax()); setRegimes(r.data || []); }
    catch(e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const seedIndia = async () => {
    setSeeding(true);
    try {
      await axios.post(`${API}/tax-config/seed-india`, {}, ax());
      flash("✅ India New & Old Regime seeded!"); load();
    } catch(e) { flash("ℹ️ " + (e.response?.data?.detail || "Already seeded")); }
    finally { setSeeding(false); }
  };

  // Regime CRUD
  const saveRegime = async () => {
    try {
      const payload = { ...rData, standard_deduction: parseFloat(rData.standard_deduction)||0 };
      if (rMode === "edit") await axios.put(`${API}/tax-config/${rId}`, payload, ax());
      else                  await axios.post(`${API}/tax-config/`, payload, ax());
      setROpen(false); load(); flash("✅ Saved");
    } catch(e) { flash("❌ " + (e.response?.data?.detail || "Error")); }
  };

  const deleteRegime = async () => {
    try { await axios.delete(`${API}/tax-config/${delId}`, ax()); setDelOpen(false); load(); }
    catch(e) { console.error(e); }
  };

  // Slab CRUD
  const saveSlab = async () => {
    try {
      const payload = {
        from_amount: parseFloat(sData.from_amount)||0,
        to_amount:   sData.to_amount === "" ? null : parseFloat(sData.to_amount)||null,
        rate:        parseFloat(sData.rate)||0,
        sort_order:  parseInt(sData.sort_order)||0,
      };
      if (sMode === "edit") await axios.put(`${API}/tax-config/slabs/${sId}`, payload, ax());
      else                  await axios.post(`${API}/tax-config/${sRegId}/slabs`, payload, ax());
      setSOpen(false); load(); flash("✅ Slab saved");
    } catch(e) { flash("❌ Error saving slab"); }
  };

  const deleteSlab = async (sid) => {
    try { await axios.delete(`${API}/tax-config/slabs/${sid}`, ax()); load(); }
    catch(e) { console.error(e); }
  };

  // Deduction CRUD
  const saveDeduction = async () => {
    try {
      await axios.post(`${API}/tax-config/${dRegId}/deductions`,
        { ...dData, max_amount: parseFloat(dData.max_amount)||0 }, ax());
      setDOpen(false); load(); flash("✅ Deduction rule saved");
    } catch(e) { flash("❌ Error"); }
  };
  const deleteDeduction = async (did) => {
    try { await axios.delete(`${API}/tax-config/deductions/${did}`, ax()); load(); }
    catch(e) { console.error(e); }
  };

  // Rebate CRUD
  const saveRebate = async () => {
    try {
      await axios.post(`${API}/tax-config/${rbRegId}/rebates`,
        { ...rbData, if_income_below: parseFloat(rbData.if_income_below)||0,
          rebate_amount: parseFloat(rbData.rebate_amount)||0 }, ax());
      setRbOpen(false); load(); flash("✅ Rebate saved");
    } catch(e) { flash("❌ Error"); }
  };

  // Surcharge CRUD
  const saveSurcharge = async () => {
    try {
      await axios.post(`${API}/tax-config/${scRegId}/surcharges`,
        { ...scData, rate: parseFloat(scData.rate)||0 }, ax());
      setScOpen(false); load(); flash("✅ Surcharge saved");
    } catch(e) { flash("❌ Error"); }
  };

  const card = { background:T.card, border:`1px solid ${T.border}`, borderRadius:12, overflow:"hidden" };

  return (
    <div style={{ fontFamily:"'Outfit',sans-serif", color:T.text }}>
      {/* Header */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:20 }}>
        <div>
          <div style={{ fontSize:11, color:T.amber, fontWeight:700, letterSpacing:"0.1em", textTransform:"uppercase", marginBottom:4 }}>PAYROLL CONFIG</div>
          <h2 style={{ fontSize:22, fontWeight:800, margin:"0 0 4px" }}>Tax Regimes & Slabs</h2>
          <p style={{ fontSize:13, color:T.muted, margin:0 }}>Configure tax rules for any country — fully editable slabs, deductions and rebates</p>
        </div>
        <div style={{ display:"flex", gap:8 }}>
          <button onClick={seedIndia} disabled={seeding}
            style={{ display:"flex", alignItems:"center", gap:6, padding:"9px 16px", borderRadius:8, border:`1px solid ${T.amber}40`, background:`${T.amber}14`, color:T.amber, fontFamily:"Outfit,sans-serif", fontSize:13, cursor:"pointer" }}>
            🇮🇳 {seeding ? "Seeding…" : "Seed India Defaults"}
          </button>
          <button onClick={() => { setRMode("add"); setRData({ name:"", country:"India", financial_year:"2024-25", standard_deduction:0, is_default:false, notes:"" }); setROpen(true); }}
            style={{ display:"flex", alignItems:"center", gap:6, padding:"9px 16px", borderRadius:8, border:"none", background:`linear-gradient(135deg,${T.cyan},${T.purple})`, color:"#fff", fontFamily:"Outfit,sans-serif", fontSize:13, fontWeight:700, cursor:"pointer" }}>
            <Plus size={14}/> New Regime
          </button>
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign:"center", padding:60, color:T.muted }}>Loading…</div>
      ) : regimes.length === 0 ? (
        <div style={{ ...card, padding:60, textAlign:"center" }}>
          <Globe size={40} style={{ color:T.dim, marginBottom:12 }}/>
          <div style={{ fontSize:16, fontWeight:600, marginBottom:8 }}>No Tax Regimes Configured</div>
          <div style={{ fontSize:13, color:T.muted, marginBottom:20 }}>Start with India defaults or create a custom regime for any country</div>
          <button onClick={seedIndia} style={{ padding:"10px 24px", borderRadius:8, border:"none", background:`linear-gradient(135deg,${T.amber},${T.red})`, color:"#fff", fontFamily:"Outfit,sans-serif", fontSize:14, fontWeight:700, cursor:"pointer" }}>
            🇮🇳 Seed India FY2024-25 Defaults
          </button>
        </div>
      ) : (
        <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
          {regimes.map(r => (
            <div key={r.id} style={card}>
              {/* Regime header */}
              <div style={{ padding:"16px 20px", display:"flex", alignItems:"center", gap:12, cursor:"pointer", borderBottom: expanded===r.id ? `1px solid ${T.border}` : "none" }}
                onClick={() => setExpanded(expanded===r.id ? null : r.id)}>
                <div style={{ fontSize:24 }}>{COUNTRY_FLAGS[r.country] || "🌍"}</div>
                <div style={{ flex:1 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                    <span style={{ fontSize:15, fontWeight:700 }}>{r.name}</span>
                    {r.is_default && <span style={{ fontSize:10, padding:"2px 7px", borderRadius:8, background:`${T.cyan}1A`, color:T.cyan }}>Default</span>}
                    <span style={{ fontSize:11, color:T.muted }}>{r.country} · FY {r.financial_year}</span>
                  </div>
                  <div style={{ fontSize:12, color:T.muted, marginTop:3 }}>
                    Standard deduction: <span style={{ color:T.green }}>₹{Number(r.standard_deduction).toLocaleString("en-IN")}</span>
                    · {r.slabs.length} slabs · {r.deductions.length} deductions · {r.rebates.length} rebates
                  </div>
                </div>
                <div style={{ display:"flex", gap:6 }} onClick={e => e.stopPropagation()}>
                  <button onClick={() => { setRMode("edit"); setRId(r.id); setRData({ name:r.name, country:r.country, financial_year:r.financial_year, standard_deduction:r.standard_deduction, is_default:r.is_default, notes:r.notes||"" }); setROpen(true); }}
                    style={{ padding:"5px 10px", borderRadius:7, border:`1px solid ${T.cyan}26`, background:`${T.cyan}0D`, color:T.cyan, cursor:"pointer", fontSize:12 }}>
                    <Edit2 size={11}/>
                  </button>
                  <button onClick={() => { setDelId(r.id); setDelOpen(true); }}
                    style={{ padding:"5px 10px", borderRadius:7, border:`1px solid ${T.red}33`, background:`${T.red}0D`, color:T.red, cursor:"pointer", fontSize:12 }}>
                    <Trash2 size={11}/>
                  </button>
                </div>
                {expanded===r.id ? <ChevronUp size={14} style={{ color:T.muted }}/> : <ChevronDown size={14} style={{ color:T.muted }}/>}
              </div>

              {expanded === r.id && (
                <div style={{ padding:"16px 20px", display:"flex", flexDirection:"column", gap:20 }}>

                  {/* Tax Slabs */}
                  <div>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10 }}>
                      <span style={{ fontSize:12, fontWeight:700, color:T.cyan, textTransform:"uppercase", letterSpacing:"0.08em" }}>Tax Slabs</span>
                      <button onClick={() => { setSRegId(r.id); setSMode("add"); setSData({ from_amount:0, to_amount:"", rate:0, sort_order:r.slabs.length+1 }); setSOpen(true); }}
                        style={{ fontSize:12, padding:"4px 10px", borderRadius:7, border:`1px solid ${T.cyan}33`, background:`${T.cyan}0F`, color:T.cyan, cursor:"pointer" }}>
                        <Plus size={11}/> Add Slab
                      </button>
                    </div>
                    <table style={{ width:"100%", borderCollapse:"collapse" }}>
                      <thead>
                        <tr>{["From","To","Rate %","Actions"].map(h => (
                          <th key={h} style={{ fontSize:10, textTransform:"uppercase", color:T.dim, padding:"6px 10px", textAlign:"left", borderBottom:`1px solid ${T.cyan}0F` }}>{h}</th>
                        ))}</tr>
                      </thead>
                      <tbody>
                        {r.slabs.sort((a,b)=>a.sort_order-b.sort_order).map(s => (
                          <tr key={s.id}>
                            <td style={{ padding:"8px 10px", fontSize:13, fontFamily:"JetBrains Mono,monospace", color:T.text, borderBottom:`1px solid ${T.cyan}0A` }}>{fmt(s.from_amount)}</td>
                            <td style={{ padding:"8px 10px", fontSize:13, fontFamily:"JetBrains Mono,monospace", color:T.muted, borderBottom:`1px solid ${T.cyan}0A` }}>{fmt(s.to_amount)}</td>
                            <td style={{ padding:"8px 10px", borderBottom:`1px solid ${T.cyan}0A` }}>
                              <span style={{ fontFamily:"JetBrains Mono,monospace", color: s.rate===0?T.dim:T.amber, fontWeight:700 }}>{s.rate}%</span>
                            </td>
                            <td style={{ padding:"8px 10px", borderBottom:`1px solid ${T.cyan}0A` }}>
                              <div style={{ display:"flex", gap:5 }}>
                                <button onClick={() => { setSRegId(r.id); setSMode("edit"); setSId(s.id); setSData({ from_amount:s.from_amount, to_amount:s.to_amount??(""), rate:s.rate, sort_order:s.sort_order }); setSOpen(true); }}
                                  style={{ fontSize:11, padding:"3px 8px", borderRadius:6, border:`1px solid ${T.cyan}26`, background:"none", color:T.cyan, cursor:"pointer" }}><Edit2 size={10}/></button>
                                <button onClick={() => deleteSlab(s.id)}
                                  style={{ fontSize:11, padding:"3px 8px", borderRadius:6, border:`1px solid ${T.red}33`, background:"none", color:T.red, cursor:"pointer" }}><Trash2 size={10}/></button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Deduction Rules */}
                  <div>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10 }}>
                      <span style={{ fontSize:12, fontWeight:700, color:T.green, textTransform:"uppercase", letterSpacing:"0.08em" }}>Deduction Rules</span>
                      <button onClick={() => { setDRegId(r.id); setDData({ name:"", max_amount:0, description:"", sort_order:r.deductions.length+1 }); setDOpen(true); }}
                        style={{ fontSize:12, padding:"4px 10px", borderRadius:7, border:`1px solid ${T.green}33`, background:`${T.green}0F`, color:T.green, cursor:"pointer" }}>
                        <Plus size={11}/> Add
                      </button>
                    </div>
                    {r.deductions.length === 0 ? (
                      <div style={{ fontSize:12, color:T.dim, padding:"8px 0" }}>No deduction rules — e.g. Section 80C, Mortgage Interest</div>
                    ) : r.deductions.map(d => (
                      <div key={d.id} style={{ display:"flex", alignItems:"center", gap:10, padding:"7px 0", borderBottom:`1px solid ${T.cyan}0A` }}>
                        <div style={{ flex:1 }}>
                          <span style={{ fontSize:13, fontWeight:500 }}>{d.name}</span>
                          {d.max_amount > 0 && <span style={{ fontSize:11, color:T.muted, marginLeft:8 }}>Max: ₹{Number(d.max_amount).toLocaleString("en-IN")}</span>}
                          {d.description && <div style={{ fontSize:11, color:T.dim, marginTop:2 }}>{d.description}</div>}
                        </div>
                        <button onClick={() => deleteDeduction(d.id)}
                          style={{ fontSize:11, padding:"3px 8px", borderRadius:6, border:`1px solid ${T.red}33`, background:"none", color:T.red, cursor:"pointer" }}><Trash2 size={10}/></button>
                      </div>
                    ))}
                  </div>

                  {/* Rebates & Surcharges */}
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
                    <div>
                      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
                        <span style={{ fontSize:12, fontWeight:700, color:T.purple, textTransform:"uppercase", letterSpacing:"0.08em" }}>Rebates</span>
                        <button onClick={() => { setRbRegId(r.id); setRbData({ name:"", if_income_below:0, rebate_amount:0, notes:"" }); setRbOpen(true); }}
                          style={{ fontSize:11, padding:"3px 8px", borderRadius:6, border:`1px solid ${T.purple}33`, background:"none", color:T.purple, cursor:"pointer" }}><Plus size={10}/> Add</button>
                      </div>
                      {r.rebates.map(rb => (
                        <div key={rb.id} style={{ fontSize:12.5, padding:"5px 0", borderBottom:`1px solid ${T.cyan}0A`, color:T.muted }}>
                          <span style={{ color:T.purple, fontWeight:500 }}>{rb.name}</span>: if income ≤ ₹{Number(rb.if_income_below).toLocaleString("en-IN")}
                          {rb.rebate_amount === 0 ? " → full waiver" : ` → ₹${Number(rb.rebate_amount).toLocaleString("en-IN")} rebate`}
                        </div>
                      ))}
                    </div>
                    <div>
                      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
                        <span style={{ fontSize:12, fontWeight:700, color:T.red, textTransform:"uppercase", letterSpacing:"0.08em" }}>Surcharges & Cess</span>
                        <button onClick={() => { setScRegId(r.id); setScData({ name:"", rate:0, applies_on:"tax" }); setScOpen(true); }}
                          style={{ fontSize:11, padding:"3px 8px", borderRadius:6, border:`1px solid ${T.red}33`, background:"none", color:T.red, cursor:"pointer" }}><Plus size={10}/> Add</button>
                      </div>
                      {r.surcharges.map(sc => (
                        <div key={sc.id} style={{ fontSize:12.5, padding:"5px 0", borderBottom:`1px solid ${T.cyan}0A`, color:T.muted }}>
                          <span style={{ color:T.red, fontWeight:500 }}>{sc.name}</span>: {sc.rate}% on {sc.applies_on}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Regime Modal */}
      {rOpen && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.6)", zIndex:9998, display:"flex", alignItems:"center", justifyContent:"center" }} onClick={() => setROpen(false)}>
          <div style={{ background:T.card, border:`1px solid ${T.border_strong}`, borderRadius:14, width:480, fontFamily:"Outfit,sans-serif", overflow:"hidden" }} onClick={e => e.stopPropagation()}>
            <div style={{ padding:"18px 24px", borderBottom:`1px solid ${T.border}`, display:"flex", justifyContent:"space-between" }}>
              <h3 style={{ margin:0, fontSize:15, fontWeight:600 }}>{rMode==="add"?"New":"Edit"} Tax Regime</h3>
              <button onClick={() => setROpen(false)} style={{ background:"none", border:"none", cursor:"pointer", color:T.muted }}><X size={15}/></button>
            </div>
            <div style={{ padding:22, display:"flex", flexDirection:"column", gap:13 }}>
              <div><label style={lbl}>Regime Name *</label><input value={rData.name} onChange={e=>setRData({...rData,name:e.target.value})} placeholder="e.g. New Regime, PAYE, Flat Tax 15%" style={inp(!rData.name)}/></div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
                <div><label style={lbl}>Country</label>
                  <select value={rData.country} onChange={e=>setRData({...rData,country:e.target.value})} style={inp()}>
                    {["India","Singapore","USA","UK","Malaysia","UAE","Other"].map(c=><option key={c}>{c}</option>)}
                  </select>
                </div>
                <div><label style={lbl}>Financial Year</label><input value={rData.financial_year} onChange={e=>setRData({...rData,financial_year:e.target.value})} placeholder="2024-25" style={inp()}/></div>
              </div>
              <div><label style={lbl}>Standard Deduction (₹)</label><input type="number" min={0} value={rData.standard_deduction} onChange={e=>setRData({...rData,standard_deduction:e.target.value})} placeholder="e.g. 75000" style={inp()}/></div>
              <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                <input type="checkbox" checked={rData.is_default} onChange={e=>setRData({...rData,is_default:e.target.checked})} id="isDefault"/>
                <label htmlFor="isDefault" style={{ fontSize:13, color:T.muted, cursor:"pointer" }}>Set as default regime for all employees</label>
              </div>
              <div><label style={lbl}>Notes</label><textarea value={rData.notes} onChange={e=>setRData({...rData,notes:e.target.value})} rows={2} placeholder="Optional description…" style={{...inp(),resize:"vertical"}}/></div>
              <div style={{ display:"flex", gap:10, justifyContent:"flex-end", paddingTop:8, borderTop:`1px solid ${T.border}` }}>
                <button onClick={() => setROpen(false)} style={{ padding:"9px 18px", borderRadius:8, border:`1px solid ${T.toggle_bg}`, background:T.inp_bg, color:T.muted, cursor:"pointer", fontFamily:"Outfit,sans-serif", fontSize:13 }}>Cancel</button>
                <button onClick={saveRegime} style={{ padding:"9px 18px", borderRadius:8, border:"none", background:`linear-gradient(135deg,${T.cyan},${T.purple})`, color:"#fff", cursor:"pointer", fontFamily:"Outfit,sans-serif", fontSize:13, fontWeight:700 }}>Save</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Slab Modal */}
      {sOpen && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.6)", zIndex:9998, display:"flex", alignItems:"center", justifyContent:"center" }} onClick={() => setSOpen(false)}>
          <div style={{ background:T.card, border:`1px solid ${T.amber}33`, borderRadius:14, width:420, fontFamily:"Outfit,sans-serif", overflow:"hidden" }} onClick={e => e.stopPropagation()}>
            <div style={{ padding:"16px 22px", borderBottom:`1px solid ${T.border}`, display:"flex", justifyContent:"space-between" }}>
              <h3 style={{ margin:0, fontSize:15, fontWeight:600 }}>{sMode==="add"?"Add":"Edit"} Tax Slab</h3>
              <button onClick={() => setSOpen(false)} style={{ background:"none", border:"none", cursor:"pointer", color:T.muted }}><X size={15}/></button>
            </div>
            <div style={{ padding:20, display:"flex", flexDirection:"column", gap:12 }}>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
                <div><label style={lbl}>From (₹)</label><input type="number" min={0} value={sData.from_amount} onChange={e=>setSData({...sData,from_amount:e.target.value})} style={inp()}/></div>
                <div><label style={lbl}>To (₹) — blank = unlimited</label><input type="number" min={0} value={sData.to_amount} onChange={e=>setSData({...sData,to_amount:e.target.value})} placeholder="Unlimited" style={inp()}/></div>
              </div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
                <div><label style={lbl}>Tax Rate (%)</label><input type="number" min={0} max={100} step={0.5} value={sData.rate} onChange={e=>setSData({...sData,rate:e.target.value})} style={inp()}/></div>
                <div><label style={lbl}>Sort Order</label><input type="number" min={0} value={sData.sort_order} onChange={e=>setSData({...sData,sort_order:e.target.value})} style={inp()}/></div>
              </div>
              {sData.from_amount !== "" && (
                <div style={{ background:`${T.amber}0F`, border:`1px solid ${T.amber}1F`, borderRadius:8, padding:"9px 14px", fontSize:13, color:T.amber }}>
                  ₹{Number(sData.from_amount).toLocaleString("en-IN")} – {sData.to_amount ? `₹${Number(sData.to_amount).toLocaleString("en-IN")}` : "∞"} @ <strong>{sData.rate}%</strong>
                </div>
              )}
              <div style={{ display:"flex", gap:10, justifyContent:"flex-end", paddingTop:8, borderTop:`1px solid ${T.border}` }}>
                <button onClick={() => setSOpen(false)} style={{ padding:"8px 16px", borderRadius:8, border:`1px solid ${T.toggle_bg}`, background:T.inp_bg, color:T.muted, cursor:"pointer", fontFamily:"Outfit,sans-serif", fontSize:13 }}>Cancel</button>
                <button onClick={saveSlab} style={{ padding:"8px 16px", borderRadius:8, border:"none", background:`linear-gradient(135deg,${T.amber},${T.red})`, color:"#fff", cursor:"pointer", fontFamily:"Outfit,sans-serif", fontSize:13, fontWeight:700 }}>Save Slab</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Deduction Rule Modal */}
      {dOpen && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.6)", zIndex:9998, display:"flex", alignItems:"center", justifyContent:"center" }} onClick={() => setDOpen(false)}>
          <div style={{ background:T.card, border:`1px solid ${T.green}33`, borderRadius:14, width:420, fontFamily:"Outfit,sans-serif", overflow:"hidden" }} onClick={e => e.stopPropagation()}>
            <div style={{ padding:"16px 22px", borderBottom:`1px solid ${T.border}`, display:"flex", justifyContent:"space-between" }}>
              <h3 style={{ margin:0, fontSize:15, fontWeight:600 }}>Add Deduction Rule</h3>
              <button onClick={() => setDOpen(false)} style={{ background:"none", border:"none", cursor:"pointer", color:T.muted }}><X size={15}/></button>
            </div>
            <div style={{ padding:20, display:"flex", flexDirection:"column", gap:12 }}>
              <div><label style={lbl}>Name *</label><input value={dData.name} onChange={e=>setDData({...dData,name:e.target.value})} placeholder="e.g. Section 80C, Mortgage Interest" style={inp(!dData.name)}/></div>
              <div><label style={lbl}>Max Amount (₹) — 0 = unlimited</label><input type="number" min={0} value={dData.max_amount} onChange={e=>setDData({...dData,max_amount:e.target.value})} style={inp()}/></div>
              <div><label style={lbl}>Description</label><input value={dData.description} onChange={e=>setDData({...dData,description:e.target.value})} placeholder="What qualifies for this deduction" style={inp()}/></div>
              <div style={{ display:"flex", gap:10, justifyContent:"flex-end", paddingTop:8, borderTop:`1px solid ${T.border}` }}>
                <button onClick={() => setDOpen(false)} style={{ padding:"8px 16px", borderRadius:8, border:`1px solid ${T.toggle_bg}`, background:T.inp_bg, color:T.muted, cursor:"pointer", fontFamily:"Outfit,sans-serif", fontSize:13 }}>Cancel</button>
                <button onClick={saveDeduction} style={{ padding:"8px 16px", borderRadius:8, border:"none", background:`linear-gradient(135deg,${T.green},${T.cyan})`, color:"#fff", cursor:"pointer", fontFamily:"Outfit,sans-serif", fontSize:13, fontWeight:700 }}>Save</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Rebate Modal */}
      {rbOpen && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.6)", zIndex:9998, display:"flex", alignItems:"center", justifyContent:"center" }} onClick={() => setRbOpen(false)}>
          <div style={{ background:T.card, border:`1px solid ${T.purple}33`, borderRadius:14, width:420, fontFamily:"Outfit,sans-serif", overflow:"hidden" }} onClick={e => e.stopPropagation()}>
            <div style={{ padding:"16px 22px", borderBottom:`1px solid ${T.border}`, display:"flex", justifyContent:"space-between" }}>
              <h3 style={{ margin:0, fontSize:15, fontWeight:600 }}>Add Tax Rebate</h3>
              <button onClick={() => setRbOpen(false)} style={{ background:"none", border:"none", cursor:"pointer", color:T.muted }}><X size={15}/></button>
            </div>
            <div style={{ padding:20, display:"flex", flexDirection:"column", gap:12 }}>
              <div><label style={lbl}>Rebate Name</label><input value={rbData.name} onChange={e=>setRbData({...rbData,name:e.target.value})} placeholder="e.g. Section 87A" style={inp()}/></div>
              <div><label style={lbl}>If Taxable Income Below (₹)</label><input type="number" min={0} value={rbData.if_income_below} onChange={e=>setRbData({...rbData,if_income_below:e.target.value})} style={inp()}/></div>
              <div><label style={lbl}>Rebate Amount (₹) — 0 = full tax waived</label><input type="number" min={0} value={rbData.rebate_amount} onChange={e=>setRbData({...rbData,rebate_amount:e.target.value})} style={inp()}/></div>
              <div style={{ display:"flex", gap:10, justifyContent:"flex-end", paddingTop:8, borderTop:`1px solid ${T.border}` }}>
                <button onClick={() => setRbOpen(false)} style={{ padding:"8px 16px", borderRadius:8, border:`1px solid ${T.toggle_bg}`, background:T.inp_bg, color:T.muted, cursor:"pointer", fontFamily:"Outfit,sans-serif", fontSize:13 }}>Cancel</button>
                <button onClick={saveRebate} style={{ padding:"8px 16px", borderRadius:8, border:"none", background:`linear-gradient(135deg,${T.purple},${T.cyan})`, color:"#fff", cursor:"pointer", fontFamily:"Outfit,sans-serif", fontSize:13, fontWeight:700 }}>Save</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Surcharge Modal */}
      {scOpen && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.6)", zIndex:9998, display:"flex", alignItems:"center", justifyContent:"center" }} onClick={() => setScOpen(false)}>
          <div style={{ background:T.card, border:`1px solid ${T.red}33`, borderRadius:14, width:380, fontFamily:"Outfit,sans-serif", overflow:"hidden" }} onClick={e => e.stopPropagation()}>
            <div style={{ padding:"16px 22px", borderBottom:`1px solid ${T.border}`, display:"flex", justifyContent:"space-between" }}>
              <h3 style={{ margin:0, fontSize:15, fontWeight:600 }}>Add Surcharge / Cess</h3>
              <button onClick={() => setScOpen(false)} style={{ background:"none", border:"none", cursor:"pointer", color:T.muted }}><X size={15}/></button>
            </div>
            <div style={{ padding:20, display:"flex", flexDirection:"column", gap:12 }}>
              <div><label style={lbl}>Name</label><input value={scData.name} onChange={e=>setScData({...scData,name:e.target.value})} placeholder="e.g. Health & Education Cess" style={inp()}/></div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
                <div><label style={lbl}>Rate (%)</label><input type="number" min={0} step={0.5} value={scData.rate} onChange={e=>setScData({...scData,rate:e.target.value})} style={inp()}/></div>
                <div><label style={lbl}>Applies On</label>
                  <select value={scData.applies_on} onChange={e=>setScData({...scData,applies_on:e.target.value})} style={inp()}>
                    <option value="tax">Tax Amount</option>
                    <option value="income">Gross Income</option>
                  </select>
                </div>
              </div>
              <div style={{ display:"flex", gap:10, justifyContent:"flex-end", paddingTop:8, borderTop:`1px solid ${T.border}` }}>
                <button onClick={() => setScOpen(false)} style={{ padding:"8px 16px", borderRadius:8, border:`1px solid ${T.toggle_bg}`, background:T.inp_bg, color:T.muted, cursor:"pointer", fontFamily:"Outfit,sans-serif", fontSize:13 }}>Cancel</button>
                <button onClick={saveSurcharge} style={{ padding:"8px 16px", borderRadius:8, border:"none", background:`linear-gradient(135deg,${T.red},${T.amber})`, color:"#fff", cursor:"pointer", fontFamily:"Outfit,sans-serif", fontSize:13, fontWeight:700 }}>Save</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Popup open={delOpen} message="Delete this tax regime and all its slabs?" handleClose={() => setDelOpen(false)} handleConfirm={deleteRegime}/>

      {toast && (
        <div style={{ position:"fixed", bottom:28, left:"50%", transform:"translateX(-50%)", padding:"10px 22px", borderRadius:10, fontSize:13, fontWeight:600, zIndex:9999,
          background:T.card, border:`1px solid ${toast.startsWith("✅")?`${T.green}4D`:`${T.amber}4D`}`,
          color:toast.startsWith("✅")?T.green:T.amber, boxShadow:T.shadow, fontFamily:"Outfit,sans-serif" }}>
          {toast}
        </div>
      )}
    </div>
  );
}
